import React, { useState, useEffect, useMemo } from "react";
import Invoice from "./OrderSections/Invoice";
import AddressSelector from "./OrderSections/AddressSelector";
import api from "../api";
import { Product } from "@flooron/shared";
import "./OrderScreen.css";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import ScreenTitle from "../components/ScreenTitle";
import Loader from "../components/Loader";

const Order: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [selectedAddressId, setSelectedAddressId] = useState<number | null | undefined>(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutData, setCheckoutData] = useState<any>(null);
  
  const { productId } = useParams<{ productId: string }>();
  
  // Get quantity from URL params, default to 1
  const quantity = parseInt(searchParams.get('quantity') || '1', 10);
  
  // Get sizeQuantities from navigation state (Buy Now flow)
  const sizeQuantitiesFromState = (location.state as any)?.sizeQuantities || {};

  const handleBack = () => {
    navigate(-1);
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // 3. Consolidated Data Fetching
  useEffect(() => {
    const loadOrderData = async () => {
      try {
        setLoading(true);
        console.log(productId)
        if (productId) {
          // Direct Purchase Path
          const data = await api.getProductsDetail(productId);
          setCheckoutData(data);
        } else {
          // Cart Path - Returns { items: [...], total: number }
          const data = await api.getCart();
          setCheckoutData(data);
        }
      } catch (error) {
        console.error("Order Load Error:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrderData();
  }, [productId]);

  // 4. Price Calculation Logic for Wholesale Tiers
  const calculatePrice = (product: Product, qty: number): number => {
    if (!product.priceTiers || product.priceTiers.length === 0) return 0;
    
    const sortedTiers = [...product.priceTiers].sort((a, b) => a.minQty - b.minQty);
    
    const matchingTier = sortedTiers.find(
      (tier) => qty >= tier.minQty && (!tier.maxQty || qty <= tier.maxQty)
    );

    return matchingTier ? matchingTier.price : sortedTiers[sortedTiers.length - 1]?.price || 0;
  };

  // 5. Convert Buy Now product to Invoice format
  const invoiceItems = useMemo(() => {
    if (!checkoutData) return [];
    if (productId) {
      // Check if product has sizes and we have size quantities from Buy Now
      const hasSizesAndData = Object.keys(sizeQuantitiesFromState).length > 0;
      
      if (hasSizesAndData) {
        // Product with sizes - create an item for each size
        const items: any[] = [];
        Object.entries(sizeQuantitiesFromState).forEach(([size, qty]: [string, any]) => {
          if (qty > 0) {
            const unitPrice = calculatePrice(checkoutData, qty);
            items.push({
              id: `${checkoutData.id}-${size}`,
              product: checkoutData,
              quantity: qty,
              size: size,
              price: unitPrice,
              total: unitPrice * qty,
            });
          }
        });
        return items;
      } else {
        // Product without sizes - single item
        const unitPrice = calculatePrice(checkoutData, quantity);
        return [
          {
            id: checkoutData.id,
            product: checkoutData,
            quantity,
            size: null,
            price: unitPrice,
            total: unitPrice * quantity,
          }
        ];
      }
    }
    // Cart path - use items as-is
    return checkoutData.items || [];
  }, [checkoutData, quantity, productId, sizeQuantitiesFromState]);

  // 6. Derived Totals
  const currentTotal = useMemo(() => {
    if (!checkoutData) return 0;
    if (productId) {
      // For Buy Now, calculate from invoiceItems
      return invoiceItems.reduce((sum: number, item: any) => sum + (item.total || 0), 0);
    }
    // Cart path - use cart total
    return checkoutData.total || 0;
  }, [checkoutData, invoiceItems, productId]);

  // 7. Action Handlers
  const handlePlaceOrder = async () => {
    if (!selectedAddressId) {
      alert("Please select a delivery address");
      return;
    }

    try {
      setIsPlacingOrder(true);
      
      let orderData;
      if (productId) {
        // Direct purchase (Buy Now)
        const items = invoiceItems.map((item: any) => ({
          productId: parseInt(productId),
          quantity: item.quantity,
          size: item.size && item.size !== 'N/A' ? item.size : undefined
        }));
        orderData = { items };
      } else {
        // Cart checkout - no body needed, uses cart items
        orderData = {};
      }

      const response = await api.placeOrder(orderData);
      
      if (response && (response as any).id) {
        alert(`🎉 Order placed successfully!\nOrder ID: ${(response as any).id}\nTotal: ₹${currentTotal.toLocaleString("en-IN")}`);
        // Navigate to orders page or home
        navigate('/orders');
      } else {
        throw new Error('Order placement failed');
      }
      
    } catch (error: any) {
      console.error('Order placement error:', error);
      let errorMessage = 'Failed to place order. Please try again.';
      
      // Handle specific backend error messages
      if (error.message) {
        const message = error.message.toLowerCase();
        
        if (message.includes('default address')) {
          if (message.includes('set a default address')) {
            errorMessage = 'Please set a default address before placing an order';
          } else if (message.includes('default address not found')) {
            errorMessage = 'Default address not found. Please update your address';
          }
        } else if (message.includes('product with id')) {
          errorMessage = 'Product not found. Please check the product and try again.';
        } else if (message.includes('product is not available')) {
          errorMessage = 'This product is currently unavailable.';
        } else if (message.includes('invalid quantity')) {
          errorMessage = 'Invalid quantity selected. Please check and try again.';
        } else if (message.includes('size is required')) {
          errorMessage = 'Size selection is required for this product.';
        } else if (message.includes('invalid size')) {
          errorMessage = 'Invalid size selected. Please choose a valid size.';
        } else if (message.includes('insufficient quantity')) {
          errorMessage = 'Insufficient stock available for this product.';
        } else if (message.includes('sizes are not enabled')) {
          errorMessage = 'Size selection is not available for this product.';
        } else if (message.includes('cart is empty')) {
          errorMessage = 'Your cart is empty. Please add items to proceed.';
        } else if (message.includes('no items to order')) {
          errorMessage = 'No items available to order. Please check your cart or selection.';
        } else if (message.includes('user not found')) {
          errorMessage = 'User session expired. Please log in again.';
        }
      }
      
      console.log(`❌ ${errorMessage}`);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  if (loading) return (
    <div className="loading-center">
      <Loader size="large" type="spinner" text="Verifying Order Details..." />
    </div>
  );

  return (
    <div className="checkout-outer">
      <ScreenTitle getHeaderTitle = {()=>"Checkout"} onBack={handleBack} />
      <div className="checkout-stack">
        
        {/* Section 1: Order Items */}
        <section className="clean-section">
          <h2 className="section-label">Order Summary</h2>
          <div className="section-box">
            {invoiceItems.length > 0 ? (
              /* UNIFIED VIEW - Using Invoice Component for Both Buy Now and Cart */
              <Invoice items={invoiceItems} />
            ) : (
              <div className="empty-state">
                <p>No items found in your order.</p>
              </div>
            )}
          </div>
        </section>

        {/* Section 2: Shipping */}
        <section className="clean-section">
          <h2 className="section-label">Shipping Address</h2>
          <div className="section-box address-list-container">
            <AddressSelector onSelect={setSelectedAddressId} selectedId={selectedAddressId} />
          </div>
        </section>

        {/* Section 3: Action Bar */}
        <div className="total-action-bar">
          <div className="total-info">
            <small>Total Payable</small>
            <div className="price-display">₹{currentTotal.toLocaleString("en-IN")}</div>
          </div>
          <button
            className={`order-btn ${!selectedAddressId ? 'disabled' : ''}`}
            onClick={handlePlaceOrder}
            disabled={!selectedAddressId || isPlacingOrder}
          >
            {isPlacingOrder ? "Processing..." : "Place Order"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Order;